import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tabs, Button, Input, Modal, Spin } from 'antd';
import {
  VerticalAlignTopOutlined,
  LeftOutlined,
  RightOutlined,
  VerticalAlignBottomOutlined,
  PlusOutlined,
  EditOutlined,
  SaveOutlined,
  RollbackOutlined,
  DeleteOutlined,
  SearchOutlined,
  LogoutOutlined,
} from '@ant-design/icons';
import { usePW220AMode } from './hooks/usePW220AMode';
import { usePW220ARecord } from './hooks/usePW220ARecord';
import { usePW220ANavigation } from './hooks/usePW220ANavigation';
import { useLookupNames } from './hooks/useLookupNames';
import TabMainInfo from './components/TabMainInfo';
import TabDetails from './components/TabDetails';
import './PW220AForm.css';
import { pw220aApi } from '../../services/pw220aApi';
import { BatchAcceptanceBridge, pw220af1AcceptanceManifest } from '../../features/acceptance';

const PW220AForm: React.FC = () => {
  const navigate = useNavigate();
  const { mode, isEditing, setAdd, setEdit, setBrowse } = usePW220AMode();
  const [activeTab, setActiveTab] = useState('1');
  const [searchText, setSearchText] = useState('');
  const { names } = useLookupNames();

  const {
    record,
    formData,
    details,
    loading,
    loadRecord,
    resetForAdd,
    revertToRecord,
    getField,
    setField,
    handleSave,
    handleDelete,
    addDetailRows,
    deleteDetailRow,
    updateDetailRow,
  } = usePW220ARecord();

  const loadWithRecord = useCallback(
    async (id: string) => {
      await loadRecord(id);
    },
    [loadRecord]
  );

  const {
    allIds,
    currentIdx,
    loadList,
    navFirst,
    navPrev,
    navNext,
    navLast,
    appendId,
    removeId,
  } = usePW220ANavigation(loadWithRecord);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const handleAdd = useCallback(() => {
    resetForAdd();
    setAdd();
    setActiveTab('1');
  }, [resetForAdd, setAdd]);

  const handleEdit = useCallback(() => {
    if (!record) return;
    // BR-V03: cannot edit approved records
    if (record.f21a0_80 === 'P') {
      Modal.warning({ title: '已核准', content: '已核准的調整單不可修改。' });
      return;
    }
    setEdit();
  }, [record, setEdit]);

  const handleCancel = useCallback(() => {
    revertToRecord();
    setBrowse();
  }, [revertToRecord, setBrowse]);

  const handleSaveClick = useCallback(async () => {
    Modal.confirm({
      title: '存檔確認',
      content: '請確認儲存本資料！',
      okText: '確認',
      cancelText: '取消',
      onOk: async () => {
        const isAdd = mode === 'add';
        await handleSave(isAdd, (saved) => {
          setBrowse();
          if (isAdd) {
            appendId(saved.f21a0_01);
          }
        });
      },
    });
  }, [mode, handleSave, setBrowse, appendId]);

  const handleDeleteClick = useCallback(() => {
    if (!record) return;
    if (record.f21a0_80 === 'P') {
      Modal.warning({ title: '已核准', content: '已核准的調整單不可刪除。' });
      return;
    }
    Modal.confirm({
      title: '刪除確認',
      content: `請確認刪除本資料！調整單「${record.f21a0_01}」將被永久刪除。`,
      okText: '刪除',
      okType: 'danger',
      cancelText: '取消',
      onOk: () =>
        handleDelete(() => {
          removeId(record.f21a0_01);
          loadList();
        }),
    });
  }, [record, handleDelete, removeId, loadList]);

  const handleSearch = useCallback(() => {
    const trimmed = searchText.trim();
    if (!trimmed) return;
    loadRecord(trimmed).catch(() => {});
  }, [searchText, loadRecord]);

  const modeLabel = mode === 'add' ? '新增' : mode === 'edit' ? '修改' : '瀏覽';
  const navInfo =
    allIds.length > 0
      ? `${currentIdx + 1} / ${allIds.length}`
      : '無資料';

  const isApproved = formData.f21a0_80 === 'P';
  const activeDetails = details.filter((d) => d.f21a1_11.trim() !== '');

  const tabItems = [
    {
      key: '1',
      label: '基本資料',
      children: (
        <TabMainInfo
          formData={formData}
          names={names}
          isEditing={isEditing}
          getField={getField}
          setField={setField}
          isApproved={isApproved}
        />
      ),
    },
    {
      key: '2',
      label: `調整明細 (${activeDetails.length})`,
      children: (
        <TabDetails
          details={details}
          isEditing={isEditing}
          onAddRows={addDetailRows}
          onDelete={deleteDetailRow}
          onUpdate={updateDetailRow}
        />
      ),
    },
  ];

  return (
    <Spin spinning={loading}>
      <div className="pw220a-form">
        <div className="page-wrapper">
          {/* Title bar */}
          <div className="page-title-bar">
            <div className="page-title-left">
              <nav className="breadcrumb-inline">
                <a
                  href="#/erp"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/erp');
                  }}
                >
                  首頁
                </a>
                <span>/</span>
                <span>庫存管理</span>
                <span>/</span>
                <span className="current">盤存調整單</span>
              </nav>
              <span className="title-separator">&mdash;</span>
              <h1>盤存調整單</h1>
              <span className="badge">220A</span>
            </div>
            <div className="page-title-right">
              <span className="mode-label">[{modeLabel}]</span>
              <span className="nav-info">{navInfo}</span>
            </div>
          </div>

          {/* PK display */}
          <div className="search-bar" style={{ marginBottom: 0 }}>
            <span style={{ fontSize: 13, color: 'rgba(0,0,0,0.45)' }}>調整單號:</span>
            <Input
              value={formData.f21a0_01 || (mode === 'add' ? '(自動產生)' : '')}
              disabled
              style={{ width: 140 }}
            />
          </div>

          {/* Search bar */}
          <div className="search-bar">
            <Input
              placeholder="輸入調整單號查詢"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onPressEnter={handleSearch}
              style={{ width: 200 }}
              allowClear
            />
            <Button icon={<SearchOutlined />} onClick={handleSearch}>
              查詢
            </Button>
          </div>

          {/* Tabs content */}
          <div className="tabs-container">
            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              items={tabItems}
              size="small"
            />
          </div>
        </div>

        {/* Fixed action bar */}
        <div className="action-bar">
          {/* Navigation */}
          <Button
            icon={<VerticalAlignTopOutlined />}
            size="small"
            onClick={navFirst}
            disabled={isEditing || currentIdx <= 0}
            title="最前"
          />
          <Button
            icon={<LeftOutlined />}
            size="small"
            onClick={navPrev}
            disabled={isEditing || currentIdx <= 0}
            title="上一筆"
          />
          <Button
            icon={<RightOutlined />}
            size="small"
            onClick={navNext}
            disabled={isEditing || currentIdx >= allIds.length - 1}
            title="下一筆"
          />
          <Button
            icon={<VerticalAlignBottomOutlined />}
            size="small"
            onClick={navLast}
            disabled={isEditing || currentIdx >= allIds.length - 1}
            title="最後"
          />

          <span className="action-separator" />

          {/* Edit actions */}
          {!isEditing ? (
            <>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                size="small"
                onClick={handleAdd}
                title="新增"
              >
                新增
              </Button>
              <Button
                icon={<EditOutlined />}
                size="small"
                onClick={handleEdit}
                disabled={!record || isApproved}
                title="修改"
              >
                修改
              </Button>
              <Button
                danger
                icon={<DeleteOutlined />}
                size="small"
                onClick={handleDeleteClick}
                disabled={!record || isApproved}
                title="刪除"
              >
                刪除
              </Button>
            </>
          ) : (
            <>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                size="small"
                onClick={handleSaveClick}
                title="存檔"
              >
                存檔
              </Button>
              <Button
                icon={<RollbackOutlined />}
                size="small"
                onClick={handleCancel}
                title="還原"
              >
                還原
              </Button>
            </>
          )}

          <span className="action-separator" />

          <Button
            icon={<LogoutOutlined />}
            size="small"
            onClick={() => navigate('/erp')}
            title="離開"
          >
            離開
          </Button>
        </div>
        <BatchAcceptanceBridge
          manifest={pw220af1AcceptanceManifest}
          records={record ? [record] : []}
          selectedKeys={record?.f21a0_01 ? [record.f21a0_01] : []}
          keyOf={(item) => item.f21a0_01}
          queryExact={(key) => pw220aApi.getByID(key)}
          lastApiResponse={record}
          loading={loading}
          onAutoFill={navFirst}
          autoFillDisabled={!record}
          screenDraft={{ master: formData, details }}
        />
      </div>
    </Spin>
  );
};

export default PW220AForm;
